import {
  useRecentCanvasesByWorkspace,
  useWorkspaceCanvases,
  useWorkspaces,
} from "@/api/workspaces";
import { UploadTarget } from "@/hooks/useFileRecordUpload";
import { Canvas, Workspace } from "@cohesive/api-client";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@cohesive/ui-components/ui/drawer";
import {
  ChevronLeft,
  ChevronRight,
  FolderOpen,
  Layers,
  Loader2,
} from "lucide-react";
import { useEffect, useState } from "react";

export interface DestinationSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the chosen destination; the sheet closes itself. */
  onSelect: (target: UploadTarget) => void;
  /** Header title at the workspace level, e.g. "Send to…" / "Save to…". */
  title: string;
  /** Row label for committing to the workspace container itself while drilled
   * into it, e.g. "Upload to workspace" / "Send to this workspace". */
  workspaceActionLabel: string;
  /** Show a "Recent canvases" section above the workspace list. */
  showRecents?: boolean;
  /** Pre-drill into this workspace when the surface is workspace-scoped. */
  initialWorkspaceId?: string;
}

/**
 * Full-height bottom sheet for picking where something lands: a workspace
 * (root) or a canvas inside it. Generalized from the mobile upload target
 * sheet so voice ("Send to…", "Save to…") and uploads share one navigation
 * pattern. Drill-ins swap in place with a back chevron — never a second
 * stacked sheet (dialog law).
 */
export function DestinationSheet({
  open,
  onOpenChange,
  onSelect,
  title,
  workspaceActionLabel,
  showRecents = false,
  initialWorkspaceId,
}: DestinationSheetProps) {
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const { data: workspaces, isLoading } = useWorkspaces();

  // Pre-drill once per open when the caller carries a workspace scope.
  useEffect(() => {
    if (!open) return;
    if (initialWorkspaceId && workspaces) {
      setWorkspace(
        workspaces.find((ws) => ws.id === initialWorkspaceId) ?? null,
      );
    }
    // Re-run when the list lands after the sheet opened.
  }, [open, initialWorkspaceId, workspaces]);

  const close = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
    if (!nextOpen) setWorkspace(null);
  };

  const choose = (target: UploadTarget) => {
    onSelect(target);
    close(false);
  };

  return (
    <Drawer open={open} onOpenChange={close}>
      <DrawerContent className="h-[92vh] max-h-[92vh]">
        <DrawerHeader className="text-left">
          <DrawerTitle className="truncate">
            {workspace ? workspace.name : title}
          </DrawerTitle>
        </DrawerHeader>
        <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-safe">
          {workspace ? (
            <CanvasList
              workspace={workspace}
              workspaceActionLabel={workspaceActionLabel}
              onBack={() => setWorkspace(null)}
              onChoose={choose}
            />
          ) : (
            <>
              {showRecents && <RecentCanvases onChoose={choose} />}
              <WorkspaceList
                workspaces={workspaces}
                isLoading={isLoading}
                onDrill={setWorkspace}
                onChoose={choose}
              />
            </>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}

const rowClass =
  "flex flex-1 items-center gap-3 rounded-lg px-3 py-3 text-left active:bg-accent";

const sectionLabelClass =
  "px-3 pb-1 pt-3 font-mono text-2xs uppercase tracking-[0.08em] text-muted-foreground";

function SheetLoading() {
  return (
    <div className="flex items-center justify-center py-8 text-muted-foreground">
      <Loader2 className="h-5 w-5 animate-spin" />
    </div>
  );
}

function RecentCanvases({
  onChoose,
}: {
  onChoose: (target: UploadTarget) => void;
}) {
  const { data: recents } = useRecentCanvasesByWorkspace();
  const { data: workspaces } = useWorkspaces();
  if (!recents || !workspaces) return null;

  const nameByWorkspace = new Map(
    workspaces.map((ws) => [ws.id, ws.is_private ? "My workspace" : ws.name]),
  );
  const rows = Object.entries(recents)
    .flatMap(([workspaceId, canvases]) =>
      canvases.map((canvas) => ({ canvas, workspaceId })),
    )
    .sort(
      (a, b) =>
        new Date(b.canvas.last_modified_at ?? b.canvas.updated_at).getTime() -
        new Date(a.canvas.last_modified_at ?? a.canvas.updated_at).getTime(),
    )
    .slice(0, 5);
  if (!rows.length) return null;

  return (
    <div className="flex flex-col">
      <p className={sectionLabelClass}>Recent canvases</p>
      <ul className="flex flex-col">
        {rows.map(({ canvas, workspaceId }) => (
          <li key={canvas.id}>
            <button
              type="button"
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left active:bg-accent"
              onClick={() =>
                onChoose({ class: "canvas", canvas_id: canvas.id })
              }
            >
              <Layers className="h-5 w-5 shrink-0 text-muted-foreground" />
              <span className="min-w-0">
                <span className="block truncate">
                  {canvas.name || "Untitled canvas"}
                </span>
                <span className="block truncate text-xs text-muted-foreground">
                  {nameByWorkspace.get(workspaceId) ?? "Workspace"}
                </span>
              </span>
            </button>
          </li>
        ))}
      </ul>
      <p className={sectionLabelClass}>Workspaces</p>
    </div>
  );
}

function WorkspaceList({
  workspaces,
  isLoading,
  onDrill,
  onChoose,
}: {
  workspaces: Workspace[] | undefined;
  isLoading: boolean;
  onDrill: (workspace: Workspace) => void;
  onChoose: (target: UploadTarget) => void;
}) {
  if (isLoading) return <SheetLoading />;
  if (!workspaces?.length) {
    return (
      <p className="px-3 py-6 text-center text-sm text-muted-foreground">
        No workspaces yet.
      </p>
    );
  }
  // "My workspace" first (it's the default chat target — make it the one-tap
  // capture target too), then alphabetical — the switcher's ordering.
  const sorted = [...workspaces].sort((a, b) =>
    a.is_private !== b.is_private
      ? Number(b.is_private) - Number(a.is_private)
      : a.name.localeCompare(b.name),
  );
  return (
    <ul className="flex flex-col pb-2">
      {sorted.map((ws) => (
        <li key={ws.id} className="flex items-center gap-1">
          <button
            type="button"
            className={rowClass}
            onClick={() =>
              onChoose({ class: "workspace", workspace_id: ws.id })
            }
          >
            <FolderOpen className="h-5 w-5 shrink-0 text-muted-foreground" />
            <span className="truncate">
              {ws.is_private ? "My workspace" : ws.name}
            </span>
          </button>
          <button
            type="button"
            className="mr-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-muted-foreground active:bg-accent"
            onClick={() => onDrill(ws)}
            aria-label={`Choose a canvas in ${ws.name}`}
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </li>
      ))}
    </ul>
  );
}

function CanvasList({
  workspace,
  workspaceActionLabel,
  onBack,
  onChoose,
}: {
  workspace: Workspace;
  workspaceActionLabel: string;
  onBack: () => void;
  onChoose: (target: UploadTarget) => void;
}) {
  const { data: canvases, isLoading } = useWorkspaceCanvases(workspace.id);
  return (
    <div className="flex flex-col pb-2">
      <button
        type="button"
        onClick={onBack}
        className="mb-1 flex items-center gap-1.5 self-start px-3 py-2 text-sm text-muted-foreground active:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" /> Workspaces
      </button>
      <button
        type="button"
        className={rowClass}
        onClick={() =>
          onChoose({ class: "workspace", workspace_id: workspace.id })
        }
      >
        <FolderOpen className="h-5 w-5 shrink-0 text-muted-foreground" />
        <span className="font-medium">{workspaceActionLabel}</span>
      </button>
      {isLoading ? (
        <SheetLoading />
      ) : canvases?.length ? (
        <ul className="flex flex-col">
          {canvases.map((canvas: Canvas) => (
            <li key={canvas.id}>
              <button
                type="button"
                className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left active:bg-accent"
                onClick={() =>
                  onChoose({ class: "canvas", canvas_id: canvas.id })
                }
              >
                <Layers className="h-5 w-5 shrink-0 text-muted-foreground" />
                <span className="truncate">
                  {canvas.name || "Untitled canvas"}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="px-3 py-4 text-sm text-muted-foreground">
          No canvases in this workspace.
        </p>
      )}
    </div>
  );
}
